<script setup lang="ts">
import { ref, computed } from 'vue';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import NextButton from '../components/NextButton.vue';
import VideoList from '../components/VideoList.vue';
import QuizterLayout from '../layout/QuizterLayout.vue';
import useLocale from '../../composables/use-locale';
import type { SelectedVideo } from '../composables/useQuizterLoader';

const props = withDefaults(
  defineProps<{
    videos: SelectedVideo[];
    loading?: boolean;
    start: (resourceIds: string[], questions?: CloseEndedQuestion[]) => void | Promise<void>;
    canSubscribe?: boolean;
    isSubscribed?: boolean;
    subscribing?: boolean;
  }>(),
  {
    loading: false,
    canSubscribe: false,
    isSubscribed: false,
    subscribing: false,
  },
);

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'toggle-subscribe'): void;
}>();

const { t } = useLocale();

const isStarting = ref(false);

const hasVideos = computed(() => props.videos.length > 0);

// Flatten the videos into the payload start needs.
const collectSelection = () => {
  const resourceIds = props.videos.map((v) => v.resourceId);
  const flatQuestions = props.videos.flatMap((v) => v.selectedQuestions ?? []);
  const questions = flatQuestions.length > 0 ? flatQuestions : undefined;
  return { resourceIds, questions };
};

const handleStartGame = async () => {
  if (!hasVideos.value) {
    return;
  }
  const { resourceIds, questions } = collectSelection();
  isStarting.value = true;
  try {
    await props.start(resourceIds, questions);
  } finally {
    isStarting.value = false;
  }
};
</script>

<template>
  <QuizterLayout :show-header="false" :show-back-button="true" back-button-pos="topLeft" @back="emit('back')">
    <div class="d-flex flex-column align-center justify-center ga-12">
      <h1 class="chalkduster-font text-white text-center text-h1">Quizter!</h1>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="white" size="48"></v-progress-circular>
      </div>

      <template v-else>
        <VideoList :videos="videos" :show-edit="false" :show-remove="false" />

        <!-- Action Buttons -->
        <div class="button-row">
          <NextButton
            size="large"
            :disabled="!hasVideos"
            :loading="isStarting"
            @click="handleStartGame"
            data-testid="quizter-start-button"
          >
            <template #prepend>
              <v-icon class="mr-2" icon="fa:fawsb fa-trophy"></v-icon>
            </template>
            {{ t('quizter.start_game') }}
          </NextButton>

          <v-btn
            v-if="canSubscribe"
            size="large"
            variant="outlined"
            color="white"
            rounded="pill"
            class="text-none chalkduster-font"
            :loading="subscribing"
            data-testid="quizter-subscribe-button"
            @click="emit('toggle-subscribe')"
          >
            <template #prepend>
              <v-icon :icon="isSubscribed ? 'fa:fawsb fa-heart-crack' : 'fa:fawsb fa-heart'"></v-icon>
            </template>
            {{ isSubscribed ? t('quizter.unsubscribe') : t('quizter.subscribe') }}
          </v-btn>
        </div>
      </template>
    </div>
  </QuizterLayout>
</template>

<style scoped lang="scss">
.button-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  width: 100%;
  max-width: 380px;
}
</style>
